const CLIENT_ERROR_ENDPOINT = "/api/v1/users/client-errors";
const DEDUPE_WINDOW_MS = 15 * 1000;
const MAX_FINGERPRINTS = 100;
const SENSITIVE_QUERY_KEY_PATTERN =
  /(token|secret|password|session|cookie|jwt|otp|code|key|authorization)/i;

function toSerializable(value, depth = 0) {
  if (value === null || value === undefined) return value ?? null;
  if (depth >= 4) {
    if (Array.isArray(value)) return `[Array(${value.length})]`;
    if (typeof value === "object") return "[Object]";
    return String(value);
  }

  if (value instanceof Error) {
    return {
      name: value.name || "Error",
      message: value.message || "",
      stack: value.stack || "",
    };
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => toSerializable(item, depth + 1));
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .slice(0, 25)
      .reduce((acc, [key, entryValue]) => {
        acc[key] = toSerializable(entryValue, depth + 1);
        return acc;
      }, {});
  }

  return value;
}

function sanitizeUrl(rawUrl = "") {
  if (!rawUrl || typeof window === "undefined") {
    return `${rawUrl || ""}`;
  }

  try {
    const parsed = new URL(rawUrl, window.location.origin);
    for (const key of parsed.searchParams.keys()) {
      if (SENSITIVE_QUERY_KEY_PATTERN.test(key)) {
        parsed.searchParams.set(key, "[REDACTED]");
      }
    }
    return parsed.toString();
  } catch (_error) {
    return `${rawUrl || ""}`;
  }
}

function inferAppContext(pathname = "") {
  if (pathname === "/app" || pathname.startsWith("/app/")) {
    return "platform";
  }

  if (
    pathname === "/" ||
    pathname.startsWith("/uniflo") ||
    pathname.startsWith("/pricing") ||
    pathname.startsWith("/plans") ||
    pathname.startsWith("/get-started")
  ) {
    return "public";
  }

  return "tenant";
}

function normalizeErrorLike(errorLike, fallbackMessage = "Unknown client error") {
  if (errorLike instanceof Error) {
    return {
      name: errorLike.name || "Error",
      message: errorLike.message || fallbackMessage,
      stack: errorLike.stack || "",
      code: errorLike.code || "",
    };
  }

  if (typeof errorLike === "string") {
    return {
      name: "Error",
      message: errorLike || fallbackMessage,
      stack: "",
      code: "",
    };
  }

  if (errorLike && typeof errorLike === "object") {
    return {
      name: errorLike.name || "Error",
      message: errorLike.message || fallbackMessage,
      stack: errorLike.stack || "",
      code: errorLike.code || "",
    };
  }

  return {
    name: "Error",
    message: fallbackMessage,
    stack: "",
    code: "",
  };
}

function buildPageContext() {
  if (typeof window === "undefined") {
    return {};
  }

  const { location, history, navigator } = window;

  return {
    context: inferAppContext(location.pathname || "/"),
    url: sanitizeUrl(location.href || ""),
    origin: location.origin || "",
    host: location.host || "",
    path: location.pathname || "",
    hash: location.hash || "",
    referrer: sanitizeUrl(document.referrer || ""),
    title: document.title || "",
    visibilityState: document.visibilityState || "",
    historyLength: history.length || 0,
    userAgent: navigator.userAgent || "",
  };
}

function buildDeviceContext() {
  if (typeof window === "undefined") {
    return {};
  }

  const { navigator, screen } = window;

  return {
    userAgent: navigator.userAgent || "",
    language: navigator.language || "",
    languages: Array.isArray(navigator.languages)
      ? navigator.languages.slice(0, 5)
      : [],
    platform: navigator.platform || "",
    onLine: typeof navigator.onLine === "boolean" ? navigator.onLine : null,
    cookieEnabled:
      typeof navigator.cookieEnabled === "boolean"
        ? navigator.cookieEnabled
        : null,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    deviceMemory: navigator.deviceMemory || null,
    viewport: {
      width: window.innerWidth || 0,
      height: window.innerHeight || 0,
    },
    screen: {
      width: screen?.width || 0,
      height: screen?.height || 0,
      availWidth: screen?.availWidth || 0,
      availHeight: screen?.availHeight || 0,
      pixelRatio: window.devicePixelRatio || 1,
    },
  };
}

const recentlyReported = new Map();

function pruneReportedFingerprints(now = Date.now()) {
  for (const [key, timestamp] of recentlyReported.entries()) {
    if (now - timestamp > DEDUPE_WINDOW_MS) {
      recentlyReported.delete(key);
    }
  }

  while (recentlyReported.size > MAX_FINGERPRINTS) {
    const oldestKey = recentlyReported.keys().next().value;
    recentlyReported.delete(oldestKey);
  }
}

function shouldSkipDuplicate(fingerprint) {
  const now = Date.now();
  pruneReportedFingerprints(now);
  const previousTimestamp = recentlyReported.get(fingerprint);
  if (previousTimestamp && now - previousTimestamp < DEDUPE_WINDOW_MS) {
    return true;
  }

  recentlyReported.set(fingerprint, now);
  return false;
}

export function reportClientError(report = {}) {
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return Promise.resolve(false);
  }

  const page = buildPageContext();
  const normalizedError = normalizeErrorLike(
    report.error,
    report.message || "Unknown client error"
  );
  const payload = {
    kind: `${report.kind || "client_error"}`.trim() || "client_error",
    source: `${report.source || "browser"}`.trim() || "browser",
    handled: report.handled !== false,
    timestamp: new Date().toISOString(),
    message: normalizedError.message,
    name: normalizedError.name,
    stack: normalizedError.stack,
    code: normalizedError.code,
    componentStack: report.componentStack || "",
    sourceFile: report.sourceFile || "",
    line: Number(report.line || 0),
    column: Number(report.column || 0),
    page,
    device: buildDeviceContext(),
    lifecycle: {
      readyState: document.readyState || "",
    },
    metadata: toSerializable(report.metadata || {}),
    error: normalizedError,
  };

  const fingerprint = [
    payload.kind,
    payload.page.path,
    payload.name,
    payload.message,
    payload.stack,
    payload.componentStack,
  ].join("|");

  if (shouldSkipDuplicate(fingerprint)) {
    return Promise.resolve(false);
  }

  return window
    .fetch(CLIENT_ERROR_ENDPOINT, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        "x-client-app-context": page.context,
      },
      body: JSON.stringify(payload),
    })
    .then(() => true)
    .catch(() => false);
}

export function installGlobalClientErrorHandlers() {
  if (typeof window === "undefined") {
    return () => {};
  }

  if (window.__UNIFLO_CLIENT_ERROR_HANDLERS_INSTALLED__) {
    return window.__UNIFLO_CLIENT_ERROR_HANDLERS_INSTALLED__;
  }

  const handleWindowError = (event) => {
    reportClientError({
      kind: "window_error",
      handled: false,
      error: event.error || {
        name: "WindowError",
        message: event.message || "Unhandled window error",
        stack: "",
      },
      sourceFile: event.filename || "",
      line: event.lineno || 0,
      column: event.colno || 0,
      metadata: {
        isTrusted: Boolean(event.isTrusted),
      },
    });
  };

  const handleUnhandledRejection = (event) => {
    reportClientError({
      kind: "unhandled_rejection",
      handled: false,
      error: normalizeErrorLike(
        event.reason,
        "Unhandled promise rejection"
      ),
      metadata: {
        reason: toSerializable(event.reason),
      },
    });
  };

  window.addEventListener("error", handleWindowError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  const cleanup = () => {
    window.removeEventListener("error", handleWindowError);
    window.removeEventListener(
      "unhandledrejection",
      handleUnhandledRejection
    );
    delete window.__UNIFLO_CLIENT_ERROR_HANDLERS_INSTALLED__;
  };

  window.__UNIFLO_CLIENT_ERROR_HANDLERS_INSTALLED__ = cleanup;
  return cleanup;
}
